// AdminVerifiedStartConfirmDialog (PE-1870) — the confirmation step that MUST
// run before an admin's "donor confirmed they texted START" attestation is
// persisted.
//
// A donor's SMS was suppressed by a genuine carrier-level STOP. PocketFlows
// has confirmed no API can restore deliverability short of the donor texting
// START back to the org's number — this dialog asserts an unverifiable claim
// (that the admin separately confirmed a START happened, e.g. by phone or
// email), so it must be a deliberate click-through, not a one-click toggle.
// Only an explicit Confirm fires the backend mutation, which stamps a
// distinct, auditable provenance source (admin_verified_start) rather than
// the plain 'dashboard' toggle source.

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type AdminVerifiedStartConfirmDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
};

export function AdminVerifiedStartConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: AdminVerifiedStartConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] w-[calc(100vw-2rem)]  font-inter">
        <DialogHeader>
          <DialogTitle>Confirm the donor texted START?</DialogTitle>
          <DialogDescription>
            This contact&apos;s text messages were unsubscribed by a reply to STOP.
            Only clear this if you have separately confirmed — outside this
            dashboard — that the donor has texted START back to your number.
            CharityStack and PocketFlows have no way to verify this
            automatically; this action is an audited attestation, not a
            re-check of the carrier block.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 gap-2">
          <Button
            variant="neutral"
            size="workspace"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button variant="primary" size="workspace" onClick={onConfirm} disabled={pending}>
            {pending ? 'Confirming…' : 'Donor confirmed they texted START'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
